'use client';

import { c as _c } from "react/compiler-runtime";
import './lightbox.scss';
import { Close as RdxClose, Content as RdxContent, Description as RdxDescription, Overlay as RdxOverlay, Portal as RdxPortal, Root as RdxRoot, Title as RdxTitle, Trigger as RdxTrigger } from '@radix-ui/react-dialog';
import Fade from 'embla-carousel-fade';
import { ReactNode, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { VideoObject } from 'schema-dts';
import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.tsx';
import { usePortalContainer } from '@sola/ark-core/contexts/portal-container.tsx';
import { cn } from '@sola/ark-core/utils/helpers.ts';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import type { FlexibleContentProps } from '@sola/schema/lib/schema.ts';
import { trackClick } from '@sola/tracking/lib/analytics/events.ts';
import { cookieConsentVideoURL } from '@sola/tracking/lib/video.ts';
import { ImageSizeIntent } from '../../atoms/image/image.tsx';
import { JsonLd } from '../../atoms/json-ld/json-ld.tsx';
import { buildVideoJsonLd } from '../../utils/schema-utils.ts';
import { Carousel, CarouselContent, CarouselItem, CarouselLazyLoadImage, CarouselLazyLoadVideo, CarouselNext, CarouselPrevious, CarouselProgress } from '../carousel/carousel.tsx';
import { LightboxButtons } from './lightbox-buttons.tsx';
export interface LightboxProps {
  items: FlexibleContentProps[];
  startIndex?: number;
  children?: ReactNode;
}
export const LightboxTrigger = t0 => {
  const $ = _c(14);
  const {
    className,
    children,
    stopPropagation: t1,
    ariaLabel,
    onClick
  } = t0;
  const stopPropagation = t1 === undefined ? false : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = ["core"];
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const {
    t
  } = useTranslation(t2);
  let t3;
  if ($[1] !== className || $[2] !== onClick || $[3] !== stopPropagation || $[4] !== t) {
    t3 = e => {
      if (stopPropagation) {
        e.stopPropagation();
      }
      trackClick(t("core:actions.open"), undefined, className, "LightboxTrigger");
      onClick?.(e);
    };
    $[1] = className;
    $[2] = onClick;
    $[3] = stopPropagation;
    $[4] = t;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const handleClick = t3;
  let t4;
  if ($[6] !== ariaLabel || $[7] !== children || $[8] !== t) {
    t4 = children ? <button type="button" aria-label={ariaLabel}>{children}</button> : <ButtonStyle size="md"><button type="button" aria-label={ariaLabel}>{t("core:actions.open")}</button></ButtonStyle>;
    $[6] = ariaLabel;
    $[7] = children;
    $[8] = t;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== className || $[11] !== handleClick || $[12] !== t4) {
    t5 = <RdxTrigger asChild={true} onClick={handleClick} className={className}>{t4}</RdxTrigger>;
    $[10] = className;
    $[11] = handleClick;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  return t5;
};

/**
 * Renders VideoObject JSON-LD structured data for lightbox video items.
 * Use this once per page/section to avoid duplicate schema markup when
 * multiple Lightbox instances share the same items array.
 */
export const LightboxVideoSchemas = t0 => {
  const $ = _c(4);
  const {
    items
  } = t0;
  let t1;
  if ($[0] !== items) {
    t1 = items.filter(_temp).map(_temp2);
    $[0] = items;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <>{t1}</>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
};
export const Lightbox = t0 => {
  const $ = _c(36);
  const {
    items,
    startIndex: t1,
    children
  } = t0;
  const startIndex = t1 === undefined ? 0 : t1;
  const {
    t
  } = useTranslation();
  const portalContainer = usePortalContainer();
  const closeButtonRef = useRef(null);
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      closeButtonRef.current?.click();
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const handleClose = t2;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = e => {
      const target = e.target as HTMLElement;
      const backgroundClasses = ["Lightbox", "Lightbox__container", "Lightbox__slide", "Video"];
      const isBackgroundClick = backgroundClasses.some(className => target.classList.contains(className));
      if (isBackgroundClick) {
        handleClose();
      }
    };
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const handleContentClick = t3;
  let t4;
  if ($[2] !== children) {
    t4 = children ?? <LightboxTrigger className="Lightbox__trigger" ariaLabel="open lightbox" />;
    $[2] = children;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <RdxOverlay className="Lightbox__overlay" onClick={handleClose} />;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== startIndex) {
    t6 = {
      align: "start",
      loop: true,
      startIndex
    };
    $[5] = startIndex;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = [Fade()];
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div className="Lightbox__toolbar--left" />;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div className="Lightbox__toolbar--center body-xl"><CarouselProgress /></div>;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  let t10;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <ArkIcon iconName="x-close" size="24" />;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  let t11;
  if ($[11] !== t) {
    t11 = t("core:actions.close");
    $[11] = t;
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  let t12;
  if ($[13] !== t11) {
    t12 = <div className="Lightbox__toolbar body-lg container mb-comfy-sm md:mb-comfy-xl">{t8}{t9}<div className="Lightbox__toolbar--right"><RdxClose ref={closeButtonRef} onClick={_temp3}>{t10}{t11}</RdxClose></div></div>;
    $[13] = t11;
    $[14] = t12;
  } else {
    t12 = $[14];
  }
  let t13;
  if ($[15] !== items.length) {
    t13 = items.length > 1 && <div className="Lightbox__arrows"><CarouselPrevious className="Lightbox__arrow Lightbox__arrow--prev" /><CarouselNext className="Lightbox__arrow Lightbox__arrow--next" /></div>;
    $[15] = items.length;
    $[16] = t13;
  } else {
    t13 = $[16];
  }
  let t14;
  if ($[17] !== items) {
    let t15;
    if ($[19] !== items.length) {
      t15 = (slide, index) => <CarouselItem key={["slide", index, slide.title].join("-")} index={index} className="Lightbox__slide pb-comfy-md xl:pb-comfy-xs" aria-label={slide.title || `${index + 1} of ${items.length}`}><div className="Lightbox__media mx-auto">{slide.media?.image && !slide.media?.video && <CarouselLazyLoadImage index={index} className="Lightbox__image" media={slide.media} sizeIntent={ImageSizeIntent.enum.hero} />}{slide.media?.video && <CarouselLazyLoadVideo index={index} className="Lightbox__video" media={slide.media} autoplay={true} />}</div><div className={cn("Lightbox__content body-sm lg:body-md w-full max-w-md mx-auto px-comfy-sm pt-comfy-sm xl:pt-comfy-lg", !(slide.body || slide.primaryCTA || slide.secondaryCTA || slide.tertiaryCTA) && "hidden")}><RdxTitle className="Lightbox__title hidden">{slide.title}</RdxTitle><RdxDescription asChild={true}><div className="Lightbox__body my-none">{slide.body}</div></RdxDescription>{(slide.primaryCTA || slide.secondaryCTA || slide.tertiaryCTA) && <div className={cn("text-center", slide.body && "mt-comfy-sm")}><LightboxButtons {...{
              primaryCTA: slide.primaryCTA,
              secondaryCTA: slide.secondaryCTA,
              tertiaryCTA: slide.tertiaryCTA,
              buttonSize: "md"
            }} inline={true} /></div>}</div></CarouselItem>;
      $[19] = items.length;
      $[20] = t15;
    } else {
      t15 = $[20];
    }
    t14 = items.map(t15);
    $[17] = items;
    $[18] = t14;
  } else {
    t14 = $[18];
  }
  let t15;
  if ($[21] !== t14) {
    t15 = <CarouselContent>{t14}</CarouselContent>;
    $[21] = t14;
    $[22] = t15;
  } else {
    t15 = $[22];
  }
  let t16;
  if ($[23] !== t12 || $[24] !== t13 || $[25] !== t15) {
    t16 = <div className="Lightbox__container darkBlue-theme pt-comfy-lg xl:pb-comfy-lg">{t12}{t13}{t15}</div>;
    $[23] = t12;
    $[24] = t13;
    $[25] = t15;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  let t17;
  if ($[27] !== t16 || $[28] !== t6) {
    t17 = <RdxContent className="Lightbox" onClick={handleContentClick}><Carousel opts={t6} plugins={t7}>{t16}</Carousel></RdxContent>;
    $[27] = t16;
    $[28] = t6;
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  let t18;
  if ($[30] !== portalContainer || $[31] !== t17) {
    t18 = <RdxPortal container={portalContainer}>{t5}{t17}</RdxPortal>;
    $[30] = portalContainer;
    $[31] = t17;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  let t19;
  if ($[33] !== t18 || $[34] !== t4) {
    t19 = <><RdxRoot>{t4}{t18}</RdxRoot></>;
    $[33] = t18;
    $[34] = t4;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  return t19;
};
function _temp(item) {
  return item.media?.video?.url && (item.media?.image?.url || item.media?.video?.thumbnailUrl);
}
function _temp2(item_0, index) {
  const video = item_0.media.video;
  const image = item_0.media?.image;
  return <JsonLd key={`lightbox-video-schema-${index}`} item={{
    "@context": "https://schema.org",
    "@type": "VideoObject",
    ...buildVideoJsonLd({
      name: video.title || item_0.title || "Video",
      thumbnailUrl: image?.url || video.thumbnailUrl,
      contentUrl: cookieConsentVideoURL(video.url),
      embedUrl: video.url,
      description: item_0.media?.caption,
      uploadDate: video.uploadDate
    })
  }} />;
}
function _temp3(e_0) {
  return e_0.stopPropagation();
}